import { FC, useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import InputAdornment from "@mui/material/InputAdornment";
import SearchIcon from "@mui/icons-material/Search";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { Segment } from "@/apis/bacchus/types";
import { getRecentSegments, type RecentSegment } from "@/utils/recentSegments";
import { lightScrollbarStyle } from "@/styles/lightScrollbarStyle";

interface SegmentSwitcherDropdownProps {
  currentSegmentId?: string;
  allSegments: Segment[];
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  onSelect: (segmentId: string) => void;
}

interface SegmentGroup {
  title: string;
  segments: Segment[];
}

const RECENT_LIMIT = 5;

/** Sort segments alphabetically by name (case-insensitive) */
const sortByName = (a: Segment, b: Segment) =>
  (a.name ?? "").toLowerCase().localeCompare((b.name ?? "").toLowerCase());

export const SegmentSwitcherDropdown: FC<SegmentSwitcherDropdownProps> = ({
  currentSegmentId,
  allSegments,
  anchorEl,
  open,
  onClose,
  onSelect,
}) => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [recentSegments, setRecentSegments] = useState<RecentSegment[]>([]);

  // Load recent segments from localStorage when dropdown opens
  useEffect(() => {
    if (open) {
      setRecentSegments(getRecentSegments());
    }
  }, [open]);

  const handleSearchChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(event.target.value);
    },
    []
  );

  const handleSegmentClick = useCallback(
    (segmentId: string) => {
      onSelect(segmentId);
      onClose();
      setSearchTerm("");
    },
    [onSelect, onClose]
  );

  const handleNewSegment = useCallback(() => {
    navigate("/segments/explore?action=new");
    onClose();
    setSearchTerm("");
  }, [navigate, onClose]);

  const handleViewAll = useCallback(() => {
    navigate("/segments");
    onClose();
    setSearchTerm("");
  }, [navigate, onClose]);

  // Client-side search filter
  const filteredSegments = useMemo(() => {
    if (!searchTerm.trim()) return allSegments;

    const searchLower = searchTerm.toLowerCase().trim();
    return allSegments.filter(
      (s) =>
        s.name?.toLowerCase().includes(searchLower) ||
        s.description?.toLowerCase().includes(searchLower)
    );
  }, [allSegments, searchTerm]);

  // Build display groups with client-side filtering
  const displayGroups = useMemo<SegmentGroup[]>(() => {
    const isSearching = searchTerm.trim().length > 0;

    // When searching, show filtered results sorted alphabetically
    if (isSearching) {
      const results = filteredSegments
        .filter((s) => s.id !== currentSegmentId)
        .sort(sortByName);

      if (results.length === 0) return [];

      return [
        {
          title: "RESULTS",
          segments: results,
        },
      ];
    }

    // Normal view: Recent first, then all others alphabetically
    const groups: SegmentGroup[] = [];
    const recentIds = new Set(recentSegments.map((r) => r.id));

    // RECENT - segments from localStorage (preserve access order)
    if (recentSegments.length > 0) {
      const recentFiltered = recentSegments
        .filter((r) => r.id !== currentSegmentId)
        .slice(0, RECENT_LIMIT);

      if (recentFiltered.length > 0) {
        groups.push({
          title: "RECENT",
          segments: recentFiltered.map((r) => ({
            id: r.id,
            name: r.name,
          })),
        });
      }
    }

    // ALL SEGMENTS - sorted alphabetically, excluding recent ones
    const allOtherSegments = allSegments
      .filter((s) => s.id !== currentSegmentId)
      .filter((s) => !recentIds.has(s.id!))
      .sort(sortByName);

    if (allOtherSegments.length > 0) {
      groups.push({
        title: "ALL SEGMENTS",
        segments: allOtherSegments,
      });
    }

    return groups;
  }, [
    searchTerm,
    filteredSegments,
    recentSegments,
    allSegments,
    currentSegmentId,
  ]);

  const hasNoResults =
    searchTerm.trim().length > 0 && filteredSegments.length === 0;

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      transformOrigin={{ horizontal: "left", vertical: "top" }}
      anchorOrigin={{ horizontal: "left", vertical: "bottom" }}
      id="segment-switcher-menu"
      slotProps={{
        paper: {
          sx: {
            width: 280,
            maxHeight: 400,
            mt: 0.5,
          },
        },
      }}
    >
      {/* Search Input */}
      <Box sx={{ px: 1.5, py: 1 }}>
        <TextField
          size="small"
          fullWidth
          placeholder="Search segments..."
          value={searchTerm}
          onChange={handleSearchChange}
          autoFocus
          id="segment-switcher-search-input"
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                </InputAdornment>
              ),
              sx: { fontSize: 13 },
            },
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              backgroundColor: "grey.50",
            },
          }}
        />
      </Box>

      <Divider />

      {/* No Results */}
      {hasNoResults && (
        <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No segments found for "{searchTerm}"
          </Typography>
        </Box>
      )}

      {/* Empty state - no segments at all */}
      {!searchTerm && allSegments.length === 0 && (
        <Box sx={{ px: 2, py: 3, textAlign: "center" }}>
          <Typography variant="body2" color="text.secondary">
            No segments yet
          </Typography>
        </Box>
      )}

      {/* Segment Groups */}
      {allSegments.length > 0 && (
        <Box
          sx={{
            maxHeight: 250,
            overflowY: "auto",
            ...lightScrollbarStyle,
          }}
        >
          {displayGroups.map((group, groupIndex) => (
            <Box key={group.title}>
              {groupIndex > 0 && <Divider />}
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                  px: 2,
                  py: 0.5,
                  display: "block",
                  fontWeight: 600,
                  fontSize: 10,
                  letterSpacing: 0.5,
                }}
              >
                {group.title}
              </Typography>
              {group.segments.map((segment) => (
                <MenuItem
                  key={segment.id}
                  onClick={() => handleSegmentClick(segment.id!)}
                  selected={segment.id === currentSegmentId}
                  id={`segment-switcher-option-${segment.id}`}
                  sx={{
                    py: 0.5,
                    minHeight: 28,
                  }}
                >
                  <ListItemText
                    primary={segment.name}
                    primaryTypographyProps={{
                      fontSize: 13,
                      noWrap: true,
                    }}
                  />
                  {segment.id === currentSegmentId && (
                    <ListItemIcon sx={{ minWidth: "auto", ml: 1 }}>
                      <CheckIcon sx={{ fontSize: 16, color: "primary.main" }} />
                    </ListItemIcon>
                  )}
                </MenuItem>
              ))}
            </Box>
          ))}
        </Box>
      )}

      <Divider />

      {/* Actions */}
      <MenuItem
        onClick={handleNewSegment}
        sx={{ py: 0.5, minHeight: 32 }}
        id="segment-switcher-new-btn"
      >
        <ListItemIcon sx={{ minWidth: 28 }}>
          <AddIcon sx={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText
          primary="New Segment"
          primaryTypographyProps={{ fontSize: 13 }}
        />
      </MenuItem>
      <MenuItem
        onClick={handleViewAll}
        sx={{ py: 0.5, minHeight: 32 }}
        id="segment-switcher-view-all-btn"
      >
        <ListItemIcon sx={{ minWidth: 28 }}>
          <ArrowForwardIcon sx={{ fontSize: 16 }} />
        </ListItemIcon>
        <ListItemText
          primary="View all segments"
          primaryTypographyProps={{ fontSize: 13 }}
        />
      </MenuItem>
    </Menu>
  );
};

export default SegmentSwitcherDropdown;
